import type { Risiko, RisikoCreate, RisikoUpdate, RisikoStatus, RiskClass, Level, Bedrohung, Asset, Massnahme } from "@iso27001/shared";
import { apiFetch } from "./client.js";
import type { AuditEntry } from "../components/AuditHistoryModal.js";

interface ListResp<T> { items: T[]; total: number; page: number; pageSize: number; }

export const risikenApi = {
    list: (params: { q?: string; status?: RisikoStatus; treatment?: string; riskClass?: RiskClass; page?: number; pageSize?: number } = {}): Promise<ListResp<Risiko>> => {
        const qs = new URLSearchParams();
        if (params.q) qs.set("q", params.q);
        if (params.status) qs.set("status", params.status);
        if (params.treatment) qs.set("treatment", params.treatment);
        if (params.riskClass) qs.set("riskClass", params.riskClass);
        if (params.page) qs.set("page", String(params.page));
        if (params.pageSize) qs.set("pageSize", String(params.pageSize));
        const url = `/api/risiken${qs.toString() ? `?${qs}` : ""}`;
        return apiFetch<ListResp<Risiko>>(url);
    },
    get: (id: number): Promise<Risiko> => apiFetch<Risiko>(`/api/risiken/${id}`),
    create: (body: RisikoCreate): Promise<Risiko> =>
        apiFetch<Risiko>("/api/risiken", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: RisikoUpdate): Promise<Risiko> =>
        apiFetch<Risiko>(`/api/risiken/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: number): Promise<null> =>
        apiFetch<null>(`/api/risiken/${id}`, { method: "DELETE" }),
    audit: (id: number): Promise<{ items: AuditEntry[] }> =>
        apiFetch<{ items: AuditEntry[] }>(`/api/risiken/${id}/audit`),
};

export const risikenLinks = {
    listMassnahmen: (riskId: number) => apiFetch<{ items: Massnahme[] }>(`/api/risiken/${riskId}/massnahmen`),
    link: (riskId: number, massnahmeId: number) =>
        apiFetch<{ riskId: number; massnahmeId: number }>(`/api/risiken/${riskId}/massnahmen/${massnahmeId}`, { method: "PUT" }),
    unlink: (riskId: number, massnahmeId: number) =>
        apiFetch<null>(`/api/risiken/${riskId}/massnahmen/${massnahmeId}`, { method: "DELETE" }),
};

export interface FormOptions {
    likelihoodLevels: Level[];
    damageLevels: Level[];
    bedrohungen: Bedrohung[];
    assets: Asset[];
}

export async function loadFormOptions(): Promise<FormOptions> {
    const [likelihoodLevels, damageLevels, bedrohungen, assets] = await Promise.all([
        apiFetch<{ items: Level[] }>("/api/likelihood-levels"),
        apiFetch<{ items: Level[] }>("/api/damage-levels"),
        apiFetch<{ items: Bedrohung[] }>("/api/bedrohungen?pageSize=200"),
        apiFetch<{ items: Asset[] }>("/api/assets?pageSize=500"),
    ]);
    return {
        likelihoodLevels: likelihoodLevels.items,
        damageLevels: damageLevels.items,
        bedrohungen: bedrohungen.items,
        assets: assets.items,
    };
}
